import { useState, useCallback, useMemo, useEffect } from 'react';
import {  
    getHasMoreOrders, 
    getOrderList, 
    getOrderQueryList, 
    getOrders, 
    getOrdersPage, 
    getOrdersRequestPage,
    getTotalOrders,
    setOrderError,
} from './OrderSlice';
import { 
    getBidQueryList,
    getBids,
    getBidsList, 
    getBidsPage, 
    getBidsRequestPage, 
    getHasMoreBids, 
    getTotalBids, 
    setBidError, 
} from '../Bids/BidSlice';
import Loading from '../../../Components/Loading';
import RenderBidItem from '../Bids/renderBidItem';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import { Bid } from '../../../Types/Bid';
import RenderOrderItem from './RenderOrderItem';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../firebase';
import { getWriterId } from '../Managers/SalesRepView/WriterSlice';
import { Button, Container, Spinner } from 'reactstrap';
import { Order } from '../../../Types/Order';
import { IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import { Clear, Refresh, Search, Sort, SwapVert } from '@mui/icons-material';
import { useInfiniteScroll } from '../../../Hooks/useInfiniteScroll';
import SortModal from './SortModal';
import { usdFormat } from '../../../Utility/usdFormat';

type Tab = 'Tab1' | 'Tab2'

export type Signature = string | null;

export type OrderOptions = 'id' | 'dateOrder' | 'dateRequired' | null;
export type BidOptions = 'quoteID' | 'created' | 'expires' | null;
export type AscDesc = 'asc' | 'desc' | null;

const OrderReport = () => {
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState<Tab>('Tab1');
    const [query, setQuery] = useState<string>('');
    const orderQueryData = useAppSelector(getOrderQueryList);
    const bidQueryData = useAppSelector(getBidQueryList);
    const [searchActive, setSearchActive] = useState<boolean>(false);
    const [sortModalOpen, setSortModalOpen] = useState<boolean>(false);
    const [orderSortBy, setOrderSortBy] = useState<OrderOptions>(null);
    const [bidSortBy, setBidSortBy] = useState<BidOptions>(null);
    const [sortDir, setSortDir] = useState<AscDesc>(null);
    const allBidData = useAppSelector(getBidsList);
    const bidData = useMemo(() => 
        allBidData.filter((bid: Bid) => bid.quoteType !== 'S'),
        [allBidData, 'S']
    );
    const totalOrders = useAppSelector(getTotalOrders);
    const totalBids = useAppSelector(getTotalBids);
    const orderData = useAppSelector(getOrderList);
    const orderLoading = useAppSelector((state) => state.orders.loading);
    const orderError = useAppSelector((state) => state.orders.error);
    const bidLoading = useAppSelector((state) => state.bids.loading);
    const bidError = useAppSelector((state) => state.bids.error);
    const hasMoreOrders = useAppSelector(getHasMoreOrders);
    const hasMoreBids = useAppSelector(getHasMoreBids);
    const orderRequestPage = useAppSelector(getOrdersRequestPage);
    const bidRequestPage = useAppSelector(getBidsRequestPage);
    const orderPage = useAppSelector(getOrdersPage);
    const bidPage = useAppSelector(getBidsPage);
    const orderInitialLoading = orderLoading && orderRequestPage === 1;
    const orderFetchingMore = orderLoading && orderRequestPage > 1;
    const bidInitialLoading = bidLoading && bidRequestPage === 1;
    const bidFetchingMore = bidLoading && bidRequestPage > 1;
    const currentWriter = useAppSelector(getWriterId);
    const openAmount = useAppSelector((state) => state.orders.openAmount);
    const completedAmount = useAppSelector((state) => state.orders.completedAmount);
    const openOrders = useAppSelector((state) => state.orders.openOrders);
    const completedOrders = useAppSelector((state) => state.orders.completedOrders);
    const [completedOnly, setCompletedOnly] = useState<'true' | 'false'>('false');
    const [filterOptions, setFilterOptions] = useState<string []>([]);

    useEffect(() => {
        dispatch(getOrders({
            page: 1,
            completedOnly,
            writerId: currentWriter,
        }));
    }, [completedOnly, currentWriter]);
    
    const onRefresh = useCallback(() => {
        if (currentWriter) {
            dispatch(getOrders({ page: 1, writerId: currentWriter, completedOnly }));
        } else {
            dispatch(getOrders({ page: 1, completedOnly }));
        }
        dispatch(setOrderError(null));
    }, [dispatch, currentWriter, completedOnly]);
    
    const onBidRefresh = useCallback(() => {
        if (currentWriter) {
            dispatch(getBids({ page: 1, writerId: currentWriter }));
        } else {
            dispatch(getBids({ page: 1 }));
        }
        dispatch(setBidError(null));
    }, []);

    const loadMoreOrderData = useCallback(() => {
        if (orderLoading || !hasMoreOrders) return;
        
        if (orderSortBy) {
            if (currentWriter) {
                dispatch(getOrders({ 
                    page: orderRequestPage + 1, 
                    sortBy: orderSortBy, 
                    sortDirection: sortDir,
                    writerId: currentWriter,
                    completedOnly: completedOnly,
                }));
            } else {
                dispatch(getOrders({ 
                    page: orderRequestPage + 1, 
                    sortBy: orderSortBy, 
                    sortDirection: sortDir,
                    completedOnly: completedOnly,
                }));
            }
        } else if (searchActive) {
            if (currentWriter) {
                dispatch(getOrders({ 
                    page: orderRequestPage + 1, 
                    lookup: query,
                    writerId: currentWriter,
                    completedOnly: completedOnly,
                }));
            } else {
                dispatch(getOrders({ 
                    page: orderRequestPage + 1, 
                    lookup: query,
                    completedOnly: completedOnly,
                }));
            }
        } else {
            if (currentWriter) {
                dispatch(getOrders({ page: orderRequestPage + 1, writerId: currentWriter, completedOnly: completedOnly, }));
            } else {
                dispatch(getOrders({ page: orderRequestPage + 1, completedOnly: completedOnly, }));
            }
        }
    }, [dispatch, orderLoading, hasMoreOrders, orderPage, query, orderSortBy, sortDir, currentWriter, completedOnly]);

    const loadMoreBidData = useCallback(() => {
        if (bidLoading || !hasMoreBids) return;

        if (bidSortBy) {
            if (currentWriter) {
                dispatch(getBids({ 
                    page: bidRequestPage + 1, 
                    sortBy: bidSortBy, 
                    sortDirection: sortDir,
                    writerId: currentWriter,
                }));
            } else {
                dispatch(getBids({ 
                    page: bidRequestPage + 1, 
                    sortBy: bidSortBy, 
                    sortDirection: sortDir 
                }));
            }
        } else if (searchActive) {
            if (currentWriter) {
                dispatch(getBids({
                    page: bidRequestPage + 1,
                    lookup: query,
                    writerId: currentWriter
                }));
            } else {
                dispatch(getBids({
                    page: bidRequestPage + 1,
                    lookup: query
                }));
            }
        } else {
            if (currentWriter) {
                dispatch(getBids({ page: bidRequestPage + 1, writerId: currentWriter }));
            } else {
                dispatch(getBids({ page: bidRequestPage + 1 }));
            }
        }
    }, [
        dispatch,
        bidLoading,
        hasMoreBids,
        bidRequestPage,
        query,
        bidSortBy,
        sortDir,
        currentWriter,
        searchActive,
    ]);

    const handleTabChange = (tab: Tab) => {
        setActiveTab(tab);
        setQuery("");
        setSearchActive(false);

        window.scrollTo({
            top: 0,
            behavior: 'auto'
        })

        if (tab === "Tab2") {
            logEvent(analytics, "bid_report_web", {
                name: "bid report",
                description: "User switched to bid view",
            });
        }
    };

    const handleOrderSort = (
        sortOption: OrderOptions,
        sortDir: AscDesc,
        filter: string [],
    ) => {
        setOrderSortBy(sortOption);
        setSortDir(sortDir);
        if (currentWriter) {
            dispatch(getOrders({ 
                page: 1, 
                sortBy: 
                sortOption, 
                sortDirection: sortDir,
                writerId: currentWriter,
                completedOnly: completedOnly,
            }))
            setFilterOptions(filter);
        } else {
            dispatch(getOrders({ page: 1, sortBy: sortOption, sortDirection: sortDir, completedOnly: completedOnly, }))
            setFilterOptions(filter);
        }
    };

    const handleBidSort = (
        sortOption: BidOptions,
        sortDir: AscDesc,
    ) => {
        setBidSortBy(sortOption);
        setSortDir(sortDir);
        if (currentWriter) {
            dispatch(getBids({ 
                page: 1, 
                sortBy: sortOption, 
                sortDirection: sortDir,
                writerId: currentWriter,
            }))
        } else {
            dispatch(getBids({ page: 1, sortBy: sortOption, sortDirection: sortDir }))
        }
    };

    const handleFilterPress = async () => {
        setSortModalOpen(true);
        logEvent(analytics, 'filter_press_web', {
            name: 'order and bid filter',
            description: 'User opened filter menu'
        });
    };

    // type NestedKeys<T> = {
    //     [K in keyof T]?: string[];
    // };

    // function handleSearch<T extends Record<string, any>>(
    //     text: string,
    //     data: T[],
    //     keys: (keyof T)[],
    //     nestedKeys: NestedKeys<T> = {}
    // ): T[] {
    //     if (!text) return data;
    //     const lower = text.toLowerCase();

    //     return data.filter(item => {
    //         const topLevelMatch = keys.some(key => {
    //             const value = item[key];
    //             return value && value.toString().toLowerCase().includes(lower);
    //         });

    //         const nestedMatch = Object.entries(nestedKeys).some(([nestedKey, fields]) => {
    //             const nested = item[nestedKey];
    //             if (!Array.isArray(nested)) return false;

    //             return nested.some(nestedItem =>
    //                 fields!.some(field => {
    //                     const nestedValue = nestedItem[field];
    //                     return nestedValue && nestedValue.toString().toLowerCase().includes(lower);
    //                 })
    //             );
    //         });

    //         return topLevelMatch || nestedMatch;
    //     });
    // };

    const handleSearchChange = (text: string) => {
        setQuery(text);
    };

    const handleSearch = async (query: string) => {
        try {
            dispatch(setOrderError(null));
            setSearchActive(true);

            if (activeTab === 'Tab1') {
                await dispatch(getOrders({ lookup: query, completedOnly: completedOnly,  }))
                logEvent(analytics, 'order_search_web', {
                    name: 'order search',
                    description: 'User searched for a order'
                });
            }
            if (activeTab === 'Tab2') {
                await dispatch(getBids({ lookup: query }))
                logEvent(analytics, 'bid_search_web', {
                    name: 'bid search',
                    description: 'User searched for a bid'
                });
            }
            
        } catch (e: any) {
            dispatch(setOrderError(e));
        }
    };

    // useEffect(() => {
    //     if (activeTab === 'Tab1') {
    //         setOrderSearchData(handleSearch<Order>(query, orderData, [
    //             'id', 'customerName', 'customerId', 'po',
    //             'branchName', 'description', 'dateOrder', 
    //             'dateRequired', 'statusMessage',
    //         ]));
    //     } else {
    //         setBidSearchData(handleSearch<Bid>(query, bidData, [
    //             'billName', 'billState', 'billZip', 'created',
    //             'expires', 'job', 'quoteID', 'shipCity', 'shipName',
    //             'shipNotes', 'shipPhone', 'shipZip', 'note', 'job',
    //         ],
    //         // { products: ['product', 'sku']}
    //     ));
    //     }
    // }, [query, orderData, bidData]);

    const renderOrderFooter = () => {
        if (!orderFetchingMore || !hasMoreOrders) return null;

        return (
            <div style={{ height: 80,}} className='justify-content-center d-flex pt-3'>
                <Spinner 
                    color="primary"
                    style={{ width: '3rem', height: '3rem'}}
                />
            </div>
        );
    };

    const renderBidFooter = () => {
        if (!bidFetchingMore || !hasMoreBids) return null;

        return (
            <div style={{ height: 80,}} className='justify-content-center d-flex pt-3'>
                <Spinner 
                    color="primary"
                    style={{ width: '3rem', height: '3rem'}}
                />
            </div>
        );
    };

    const orderSentinelRef = useInfiniteScroll({
        onEndReached: loadMoreOrderData,
        enabled:
            activeTab === "Tab1" &&
            !orderLoading &&
            hasMoreOrders,
    });

    const bidSentinelRef = useInfiniteScroll({
        onEndReached: loadMoreBidData,
        enabled:
            activeTab === "Tab2" &&
            !bidLoading &&
            hasMoreBids,
    });

    const tabContent = () => {
        switch (activeTab) {
            case 'Tab1':

                return (
                    <div style={{ flex: 1}}>
                        {orderInitialLoading ? (<Loading />) 
                        : orderData.length === 0
                        ? (
                            <div key="orders">
                                <span className='headerText'>No Open Orders</span>
                            </div>
                        ) : (
                            <div>
                                {(
                                    searchActive 
                                    ? orderQueryData 
                                    : filterOptions.length
                                    ? (orderData.filter((order: Order) => filterOptions.includes(order.statusMessage)))
                                    : orderData
                                ).map((item: Order) => (
                                    <RenderOrderItem 
                                        item={item}
                                        onError={(error) => dispatch(setOrderError(error))}
                                    />
                                ))}
                                <div 
                                    ref={orderSentinelRef}
                                    style={{
                                        minHeight: 20,
                                        width: '100%',
                                    }}
                                />
                                {renderOrderFooter()}
                            </div>
                        )}
                    </div>
                )
            case 'Tab2':

                return (
                    <div style={{ flex: 1}}>
                        
                        {bidInitialLoading ? (<Loading />)
                        : bidData.length === 0
                        ? (
                            <div key="bids">
                                <span className='headerText'>No Open Bids</span>
                            </div>
                        ) : (
                            <div>
                                {(searchActive ? bidQueryData : bidData).map((item: Bid) => (
                                    <RenderBidItem 
                                        item={item}
                                        onError={(error) => dispatch(setBidError(error))}
                                    />
                                ))}
                                <div 
                                    ref={bidSentinelRef}
                                    style={{
                                        minHeight: 20,
                                        width: '100%',
                                    }}
                                />
                                {renderBidFooter()}
                            </div>
                        )}
                    </div>
                )
            default:
                return null;
        }
    };

    return (
        <Container>
            <div
                style={{
                    position: 'sticky',
                    top: 60,
                    zIndex: 1000,
                    paddingBlock: '10px',
                }}
                className='bg-light'
            >
                <div 
                    style={{
                        display: 'flex',
                        marginBlock: 10,
                        marginInline: 10
                    }}
                >
                    <Button
                        className={activeTab === 'Tab1' ? `tabButton bg-primary` : `tabButton bg-white`}
                        onClick={() => handleTabChange("Tab1")}    
                    >
                        <span style={{ fontWeight: 'bold'}}>Orders ({totalOrders})</span>
                    </Button>
                    <Button
                        className={activeTab === 'Tab2' ? `tabButton bg-primary` : `tabButton bg-white`}
                        onClick={() => handleTabChange("Tab2")}   
                    >
                        <span style={{ fontWeight: 'bold'}}>Bids ({totalBids})</span>
                    </Button>
                </div>
                <div style={{ display: 'flex', marginInline: 10, alignItems: 'center', justifyContent: 'center', gap: 20 }}>
                    <div 
                        onClick={() => {
                            setQuery('');
                            if (activeTab === 'Tab1') onRefresh();
                            if (activeTab === 'Tab2') onBidRefresh();

                            setSearchActive(false);
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        <Refresh />
                    </div>
                    <OutlinedInput 
                        id='search'
                        style={{ width: '75%' }}
                        value={query}
                        className='bg-white shadow-sm'
                        onChange={(e) => handleSearchChange(e.target.value)}
                        placeholder='Search'
                        autoCapitalize='none'
                        autoCorrect='none'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch(query);
                            }
                        }}
                        startAdornment={
                            <InputAdornment position='start'>
                                <IconButton
                                    edge='start'
                                >
                                    {query ? <Search /> : null}
                                </IconButton>
                            </InputAdornment>
                        }
                        endAdornment={
                            <InputAdornment position='end'>
                                <IconButton
                                    onClick={() => {
                                        setQuery('');
                                        if (activeTab === 'Tab1') onRefresh();
                                        if (activeTab === 'Tab2') onBidRefresh();

                                        setSearchActive(false);
                                    }}
                                    edge='end'
                                >
                                    {query ? <Clear /> : null}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                    {
                        !query &&
                        <div
                            style={{ display: 'flex', alignItems: 'flex-end', cursor: 'pointer' }}
                            onClick={handleFilterPress}
                        >
                            <Sort />
                        </div>
                    }
                </div>
                {activeTab === 'Tab1' && !searchActive &&
                    <div
                        style={{
                            display: 'flex',
                            marginBlock: 10,
                            marginInline: 10
                        }}
                    >
                        <Button
                            className={completedOnly === 'false' ? `tabButton bg-primary` : `tabButton bg-white`}
                            onClick={() => setCompletedOnly('false')}
                        >
                            <span style={{ fontWeight: 'bold'}}>Open ({openOrders})</span>
                        </Button>
                        <Button
                            className={completedOnly === 'true' ? `tabButton bg-primary` : `tabButton bg-white`}
                            onClick={() => {
                                if (filterOptions.length > 0) {
                                    setFilterOptions([]);
                                }
                                setCompletedOnly('true');
                            }}
                        >
                            <span style={{ fontWeight: 'bold'}}>Complete ({completedOrders})</span>
                        </Button>
                    </div>
                }
                {activeTab === 'Tab1' && !searchActive &&
                    <div>

                        <span
                            style={{
                                fontWeight: 'bold',
                                textAlign: 'center'
                            }}
                        >
                            {
                                completedOnly === 'false'
                                ? `Open Amount: ${usdFormat.format(openAmount)}`
                                : `Completed Amount: ${usdFormat.format(completedAmount)}`
                            }
                        </span>
                        {filterOptions.length > 0 &&
                            <div 
                                style={{ 
                                    flexDirection: 'row', 
                                    alignItems: 'center',
                                    backgroundColor: 'lightgray',
                                    width: '25%',
                                    borderRadius: 10,
                                    marginLeft: 10,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                }}
                                onClick={() => {
                                    setFilterOptions([]);
                                    onRefresh();
                                }}
                            >
                                <Clear />
                                <span>Clear Filter</span>
                            </div>
                        }
                    </div>
                }
            </div>
            <div style={{ paddingBottom: 10, left: 10 }}>
                {orderError
                    ? (<span style={{ color: 'red' }}>Order Error: {orderError}</span>)
                    : (null)
                }
                {bidError
                    ? (<span style={{ color: 'red' }}>Bid Error: {bidError}</span>)
                    : (null)
                }
            </div>
            <div>
                {tabContent()}
            </div>
            <SortModal 
                visible={sortModalOpen}
                activeTab={activeTab}
                onClose={() => setSortModalOpen(false)}
                onOrderSort={handleOrderSort}
                onBidSort={handleBidSort}
                filteredItems={filterOptions}
            />
        </Container>
    );
};

export default OrderReport;